import type { CollectionEntryPatch } from '../hooks/useCollection';
import type { CollectionEntry, RiftboundCard } from '../types';

/** Riftbound print tiers that only exist as foil copies (no non-foil variant). */
export const FOIL_ONLY_RARITIES = new Set(['Rare', 'Epic', 'Showcase', 'Promo'] as const);

export type FoilOnlyRarity = 'Rare' | 'Epic' | 'Showcase' | 'Promo';

export function isFoilOnlyRarity(rarity?: string | null): boolean {
  return typeof rarity === 'string' && FOIL_ONLY_RARITIES.has(rarity as FoilOnlyRarity);
}

export function cardIsFoilOnly(card: Pick<RiftboundCard, 'rarity'>): boolean {
  return isFoilOnlyRarity(card.rarity);
}

/** Effective owned counts for UI — foil-only prints always display as fully foil. */
export function effectiveCollectionQuantities(
  card: Pick<RiftboundCard, 'rarity'>,
  entry?: Pick<CollectionEntry, 'quantity' | 'quantityFoil'>,
): { quantity: number; quantityFoil: number; nonFoil: number } {
  const quantity = entry?.quantity ?? 0;
  const quantityFoil = entry?.quantityFoil ?? 0;
  if (!cardIsFoilOnly(card)) {
    return {
      quantity,
      quantityFoil,
      nonFoil: Math.max(0, quantity - quantityFoil),
    };
  }
  return { quantity, quantityFoil: quantity, nonFoil: 0 };
}

/** Ensure collection writes treat foil-only prints as fully foil. */
export function collectionPatchForFoilOnly(
  rarity: string | undefined,
  patch: CollectionEntryPatch,
): CollectionEntryPatch {
  if (!isFoilOnlyRarity(rarity)) return patch;

  if (patch.quantityDelta != null && patch.quantityFoilDelta == null) {
    return { ...patch, quantityFoilDelta: patch.quantityDelta };
  }
  if (patch.quantity != null && patch.quantityFoil == null) {
    return { ...patch, quantityFoil: patch.quantity };
  }
  return patch;
}

export function collectionPatchForCard(
  card: Pick<RiftboundCard, 'rarity'>,
  patch: CollectionEntryPatch,
): CollectionEntryPatch {
  return collectionPatchForFoilOnly(card.rarity, patch);
}
